import { z } from 'zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SignInCredentials, signInCredentialsSchema } from '../types';
import { useAuthContext } from '../context';

import { Input } from '@web/components/ui/input';
import { Button } from '@web/components/ui/button';
import { Checkbox } from '@web/components/ui/checkbox';
import { Separator } from '@web/components/ui/separator';
import {
  Card,
  CardTitle,
  CardHeader,
  CardContent,
  CardDescription,
  CardFooter,
} from '@web/components/ui/card';
import {
  Form,
  FormItem,
  FormField,
  FormLabel,
  FormControl,
  FormMessage,
} from '@web/components/ui/form';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Sparkles,
  MessageSquare,
  CreditCard,
  FileText,
} from 'lucide-react';

// Import SVG as URL
import logoSvg from './logo.svg';

// ----------------------------------------------------------------------

export function SignInForm() {
  const { signIn, removeSessionId } = useAuthContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<SignInCredentials>({
    resolver: zodResolver(signInCredentialsSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof signInCredentialsSchema>) => {
    setIsSubmitting(true);
    removeSessionId();

    try {
      const success = await signIn({
        email: values.email,
        password: values.password,
      });

      if (!success) {
        toast.error('Invalid email or password', {
          description: 'Please, try again',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderForm = (
    <div className="w-full max-w-md">
      <div className="rounded-[32px] bg-white p-10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)]">
        <Form {...form}>
          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-gray-700">
                    Email
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="text-primary/70 absolute left-4 top-3.5 h-5 w-5" />
                      <Input
                        placeholder="email@example.com"
                        className="h-12 rounded-xl border-gray-200 pl-12 text-base shadow-sm"
                        autoComplete="email"
                        {...field}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="font-medium text-gray-700">
                      Password
                    </FormLabel>
                    <a
                      href="#"
                      className="text-primary text-xs hover:underline"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <FormControl>
                    <div className="relative">
                      <Lock className="text-primary/70 absolute left-4 top-3.5 h-5 w-5" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        className="h-12 rounded-xl border-gray-200 pl-12 pr-12 text-base shadow-sm"
                        autoComplete="current-password"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                className="text-primary rounded border-gray-300"
              />
              <label htmlFor="remember" className="text-sm text-gray-700">
                Remember me
              </label>
            </div>

            <div className="flex w-full items-center justify-center py-10 pb-0">
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90 w-3/4 rounded-xl text-base font-medium"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </div>
          </form>
        </Form>
      </div>

      <div className="mt-8">
        <div className="mb-6 flex w-full items-center gap-2 text-sm">
          <div className="h-[1px] flex-1 bg-gray-200"></div>
          <span className="px-2 text-gray-500">OR</span>
          <div className="h-[1px] flex-1 bg-gray-200"></div>
        </div>

        <p className="text-center text-sm text-gray-500">
          Don't have an account?{' '}
          <a href="#" className="text-primary font-medium hover:underline">
            Sign Up
          </a>
        </p>
      </div>
    </div>
  );

  // Removed this renderInfo section as it's now directly in the return JSX

  return (
    <div className="flex min-h-screen w-full flex-col overflow-hidden lg:flex-row">
      {/* Left side - Info section - Hidden on mobile and tablet */}
      <div className="from-primary/5 to-primary/10 relative hidden bg-gradient-to-br lg:block lg:w-1/2">
        {/* Pattern background - commented out as it may not be available in your design system */}
        {/* <div className="absolute inset-0 pattern-dots pattern-primary/20 pattern-bg-transparent pattern-size-4"></div> */}
        <div className="to-primary/5 absolute right-0 top-0 h-full w-full bg-gradient-to-br from-transparent"></div>

        <div className="relative flex h-full max-w-[800px] flex-col justify-center px-12 lg:px-16 xl:px-24">
          <div className="mb-12">
            <div className="mb-6 flex items-center">
              <img
                src={logoSvg}
                alt="BeautifyCRM Logo"
                className="mr-4 h-16 w-16"
              />
              <h1 className="text-primary text-4xl font-bold">BeautifyCRM</h1>
            </div>
            <p className="text-muted-foreground mb-8 max-w-md text-xl leading-relaxed">
              Smart enterprise software tailored for the plastic surgery
              industry, helping practices efficiently manage data and scale.
            </p>
          </div>

          <div className="space-y-8">
            <div className="flex transform items-start rounded-xl bg-white/50 p-4 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:shadow-md">
              <div className="bg-primary/10 mr-5 rounded-full p-3">
                <MessageSquare className="text-primary h-7 w-7" />
              </div>
              <div>
                <h3 className="mb-2 text-lg font-semibold">
                  Communication Module
                </h3>
                <p className="text-muted-foreground">
                  Direct messaging with SMS and AI-suggested replies in your
                  tone of voice.
                </p>
              </div>
            </div>

            <div className="flex transform items-start rounded-xl bg-white/50 p-4 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:shadow-md">
              <div className="bg-primary/10 mr-5 rounded-full p-3">
                <CreditCard className="text-primary h-7 w-7" />
              </div>
              <div>
                <h3 className="mb-2 text-lg font-semibold">
                  Electronic Payments
                </h3>
                <p className="text-muted-foreground">
                  Secure credit/debit card transactions with NMI integration.
                </p>
              </div>
            </div>

            <div className="flex transform items-start rounded-xl bg-white/50 p-4 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:shadow-md">
              <div className="bg-primary/10 mr-5 rounded-full p-3">
                <FileText className="text-primary h-7 w-7" />
              </div>
              <div>
                <h3 className="mb-2 text-lg font-semibold">
                  Digital Contracts
                </h3>
                <p className="text-muted-foreground">
                  Generate and track contracts with e-signature integration.
                </p>
              </div>
            </div>

            <div className="flex transform items-start rounded-xl bg-white/50 p-4 shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:shadow-md">
              <div className="bg-primary/10 mr-5 rounded-full p-3">
                <Sparkles className="text-primary h-7 w-7" />
              </div>
              <div>
                <h3 className="mb-2 text-lg font-semibold">VernisAI Engine</h3>
                <p className="text-muted-foreground">
                  Extract key insights and tailor your sales approach based on
                  customer sentiment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form section */}
      <div className="flex w-full flex-col items-center justify-center p-4 sm:p-6 lg:w-1/2 lg:p-10">
        {/* Mobile and tablet only logo and info */}
        <div className="mb-8 w-full lg:hidden">
          <div className="mb-6 flex items-center justify-center">
            <img
              src={logoSvg}
              alt="BeautifyCRM Logo"
              className="mr-2 h-10 w-10"
            />
            <h1 className="text-primary text-2xl font-bold">BeautifyCRM</h1>
          </div>

          <p className="text-muted-foreground mx-auto mb-6 max-w-md text-center text-sm">
            Smart enterprise software tailored for the plastic surgery industry,
            helping practices efficiently manage data and scale.
          </p>

          {/* Mobile feature highlights - condensed version */}
          <div className="mx-auto mb-8 grid max-w-md grid-cols-2 gap-4">
            <div className="bg-muted/50 flex flex-col items-center rounded-lg p-3 text-center">
              <MessageSquare className="text-primary mb-2 h-5 w-5" />
              <span className="text-xs font-medium">Smart Communication</span>
            </div>
            <div className="bg-muted/50 flex flex-col items-center rounded-lg p-3 text-center">
              <CreditCard className="text-primary mb-2 h-5 w-5" />
              <span className="text-xs font-medium">Secure Payments</span>
            </div>
            <div className="bg-muted/50 flex flex-col items-center rounded-lg p-3 text-center">
              <FileText className="text-primary mb-2 h-5 w-5" />
              <span className="text-xs font-medium">Digital Contracts</span>
            </div>
            <div className="bg-muted/50 flex flex-col items-center rounded-lg p-3 text-center">
              <Sparkles className="text-primary mb-2 h-5 w-5" />
              <span className="text-xs font-medium">AI Insights</span>
            </div>
          </div>
        </div>

        {renderForm}
      </div>
    </div>
  );
}
